/* drl-benefits-timeline — deterministic scroll-position reveal +
   A/B video buffer crossfade. Custom element <benefits-timeline>. */

const DEFAULT_READING_LINE = 0.45; // where on screen each milestone activates — overridable via data-reading-line

class BenefitsTimeline extends HTMLElement {
  connectedCallback() {
    this.milestones = Array.from(this.querySelectorAll('.benefits-timeline__milestone'));
    if (this.milestones.length === 0) return;

    const parsedLine = parseFloat(this.dataset.readingLine);
    this.readingLine = Number.isFinite(parsedLine) ? parsedLine : DEFAULT_READING_LINE;

    this.videoA = this.querySelector('[data-bt-buffer="a"]');
    this.videoB = this.querySelector('[data-bt-buffer="b"]');
    this.caption = this.querySelector('[data-bt-caption]');
    this.activeBuffer = 'a';
    this.activeIndex = -1;
    this.tickQueued = false;

    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.revealOff = this.dataset.reveal === 'off';
    this.editorMode = !!(window.Shopify && window.Shopify.designMode);
    this.mobileMQ = window.matchMedia('(max-width: 749px)');
    this.isMobile = this.mobileMQ.matches;
    this.mobileMode = this.dataset.mobileMedia === 'single' ? 'single' : 'inline';

    // Mobile inline mode: each milestone has its own inline clip — viewport-gated lazy load.
    // Mobile single mode: keep the top A/B frame; play first clip only, never swap.
    // Desktop (>=750px): A/B crossfade as usual.
    if (this.isMobile && this.mobileMode === 'inline') {
      this.setupInlineClips();
    }

    if (this.revealOff || this.reduceMotion || this.editorMode) {
      this.milestones.forEach((m) => m.classList.add('is-active'));
      this.setActiveMilestone(0, { instant: true });
      return;
    }

    this.milestones[0].classList.add('is-active');
    this.setActiveMilestone(0, { instant: true });

    this.onScroll = this.onScroll.bind(this);
    this.compute = this.compute.bind(this);
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });
    this.compute();
  }

  disconnectedCallback() {
    if (this.onScroll) {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
    }
  }

  onScroll() {
    if (this.tickQueued) return;
    this.tickQueued = true;
    requestAnimationFrame(this.compute);
  }

  compute() {
    this.tickQueued = false;
    const vh = window.innerHeight;
    const rect = this.getBoundingClientRect();
    if (rect.bottom <= 0 || rect.top >= vh) return; // section off-screen — no work

    // Each milestone activates when its own top edge crosses the reading line.
    // Vertical spacing between milestones (controlled by the reveal_pace setting
    // via CSS min-height) drives the scroll distance between activations.
    const lineY = vh * this.readingLine;
    let activeIdx = 0;
    for (let i = 0; i < this.milestones.length; i++) {
      const top = this.milestones[i].getBoundingClientRect().top;
      if (top <= lineY) activeIdx = i;
    }

    // Progressive unlock — never re-fade
    for (let i = 0; i <= activeIdx; i++) {
      if (!this.milestones[i].classList.contains('is-active')) {
        this.milestones[i].classList.add('is-active');
      }
    }
    if (activeIdx !== this.activeIndex) {
      this.setActiveMilestone(activeIdx);
    }
  }

  setActiveMilestone(idx, { instant = false } = {}) {
    const m = this.milestones[idx];
    if (!m) return;
    this.activeIndex = idx;

    // Mobile inline mode: each milestone has its own inline clip — skip A/B entirely.
    if (this.isMobile && this.mobileMode === 'inline') return;
    // Mobile single mode: only act on the initial mount call; never swap as user scrolls.
    if (this.isMobile && this.mobileMode === 'single' && !instant) return;

    const src = m.dataset.clipSrc;
    const poster = m.dataset.clipPoster;
    const captionText = m.dataset.clipCaption || '';

    if (!this.videoA || !this.videoB) return;

    if (!src) {
      // Poster-only milestone (static image, no video): show the poster on the
      // active buffer. A <video> with a poster and no src displays the poster.
      if (poster) {
        const target = this.activeBuffer === 'a' && !instant ? this.videoB : this.videoA;
        const other  = target === this.videoA ? this.videoB : this.videoA;
        target.poster = poster;
        target.removeAttribute('src');
        target.dataset.currentSrc = '';
        target.classList.add('is-visible');
        if (other && other !== target) other.classList.remove('is-visible');
        this.activeBuffer = target === this.videoA ? 'a' : 'b';
      }
      this.updateCaption(captionText);
      return;
    }

    const target = this.activeBuffer === 'a' && !instant ? this.videoB : this.videoA;
    const other  = target === this.videoA ? this.videoB : this.videoA;

    if (poster) target.poster = poster;
    if (target.dataset.currentSrc !== src) {
      target.src = src;
      target.dataset.currentSrc = src;
      try { target.load(); } catch (_) {}
    }

    const playPromise = target.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => { /* autoplay blocked — harmless */ });
    }

    target.classList.add('is-visible');
    if (other && other !== target) {
      other.classList.remove('is-visible');
      window.setTimeout(() => { try { other.pause(); } catch (_) {} }, 400);
    }
    this.activeBuffer = target === this.videoA ? 'a' : 'b';

    this.updateCaption(captionText);
  }

  updateCaption(text) {
    if (!this.caption) return;
    if (text) {
      this.caption.textContent = text;
      this.caption.hidden = false;
    } else {
      this.caption.textContent = '';
      this.caption.hidden = true;
    }
  }

  setupInlineClips() {
    const inlines = Array.from(this.querySelectorAll('[data-bt-inline]'));
    if (inlines.length === 0) return;

    const ensureSrc = (v) => {
      if (!v.src && v.dataset.src) {
        v.src = v.dataset.src;
        try { v.load(); } catch (_) {}
      }
    };

    if (!('IntersectionObserver' in window)) {
      // Old browsers: just load + play all
      inlines.forEach((v) => {
        ensureSrc(v);
        v.play().catch(() => {});
      });
      return;
    }

    this.inlineObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const v = entry.target;
        if (entry.isIntersecting) {
          ensureSrc(v);
          v.play().catch(() => { /* autoplay blocked — show poster */ });
        } else {
          try { v.pause(); } catch (_) {}
        }
      });
    }, { rootMargin: '100% 0px 100% 0px', threshold: 0 });

    inlines.forEach((v) => this.inlineObserver.observe(v));
  }
}

if (!customElements.get('benefits-timeline')) {
  customElements.define('benefits-timeline', BenefitsTimeline);
}
